// @flow
import React from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import { Helmet } from 'react-helmet';
import ClientPage from '../context/clientPage';
import ClientInfo from '../context/clientInfo';
import { hasInvalidInputs, isEnabled, padCardNumber, prefixCardNumber } from '../../helpers/validateWBI';
import i18n from '../../i18n';
import { getLocale } from '../../helpers/translation';

type Location = {
  hash: string,
  pathname: string,
  search: string,
  state: any
};

type Touched = {
  cardNo: boolean,
  pin: boolean
};

type Focused = {
  cardNo: boolean,
  pin: boolean
};

type Props = {
  location: Location
};

type State = {
  pin: string,
  cardNo: string,
  prefixConfig: Object,
  paddingConfig: Object,
  cardLength: string | Array<number>,
  pinLength: string | Array<number>,
  pinTruncate: string,
  pinType: string,
  captchaRef: { current: null | HTMLDivElement },
  errorMsg: string,
  touched: Touched,
  focused: Focused,
  showError: boolean,
  showResults: boolean,
  scrollBottom: boolean,
  callEnhanced: boolean,
  balance: string,
  transactions: Array<Object>,
  requestedCurrency: string,
  [name: string]: ?boolean,
  landingTime: number
};

type SubmitData = {
  host: string,
  cardNo: string,
  pin: string,
  callEnhanced: boolean,
  showCaptcha: number,
  clearCardNumberOnError: boolean,
  clearCardNumberOnSuccess: boolean,
  formatCardNum: Object,
  token: string,
};

const withClientPage = (PassedComponent: Function, PassedProps: Object) => {
  class WithClientPage extends React.Component<Props, State> {
    state = {
      host: '',
      pin: '',
      cardNo: '',
      padding: '',
      cardLength: '',
      pinLength: '',
      pinTruncate: '',
      pinType: '',
      captchaRef: React.createRef(),
      passedCaptcha: false,
      passedCaptchaValue: '',
      errorMsg: 'error.default',
      touched: {
        cardNo: false,
        pin: false,
      },
      focused: {
        cardNo: false,
        pin: false,
      },
      showError: false, // not good response
      showResults: false, // good response
      scrollBottom: false,
      callEnhanced: false,
      showCaptcha: 1, // do not change default one here
      clearCardNumberOnError: true, // default
      clearCardNumberOnSuccess: true, // default
      balance: '',
      transactions: [],
      requestedCurrency: 'USD', // default
      applepassLink: '',
      gpayLink: '',
      thirdPartyLink: '',
      cardEOV: '',
      landingTime: new Date().getTime(),
      loadingResults: false,
      formatCardNum: { enabled: false, digitsBetweenSpaces: 0 },
      paddingConfig: { paddingCharacter: '', padUpToLength: null },
      prefixConfig: { lengthWithPrefix: 0, prefixString: '', exemptLengths: [] }
    };

    static defaultProps = {
      title: 'Balance Inquiry',
      favicon: '/favicon.ico',
      callTranslations: false,
    };

    componentDidMount(): void {
      const newProps = { ...this.props, ...PassedProps };
      const { errorMsg } = newProps;
      if (errorMsg !== undefined) this.setState({ errorMsg });
    }

    componentDidUpdate() {
      if (this.state.scrollBottom) {
        this.scrollToBottom();
        this.setState({ scrollBottom: false });
      }
    }

    handleSubmit = (data: SubmitData) => {
      const {
        cardNo, pin, host, callEnhanced, showCaptcha, clearCardNumberOnError, clearCardNumberOnSuccess, formatCardNum = { enabled: false, digitsBetweenSpaces: 0 },
      } = data;

      let cardNoNoSpace = cardNo;
      if (formatCardNum.enabled) {
        cardNoNoSpace = cardNo.replace(/ /g, '');
      }

      this.setState({
        cardNo: cardNoNoSpace,
        pin,
        host,
        callEnhanced,
        showCaptcha,
        clearCardNumberOnError,
        clearCardNumberOnSuccess,
        formatCardNum,
      });

      const revisedData = {
        ...data,
        cardNo: cardNoNoSpace,
      };

      if (showCaptcha === 2) {
        /* execute captcha for invisible */
        this.state.captchaRef.current.execute();
      } else {
        this.handleSubmitCallBack(revisedData);
      }
    };

    handleSubmitCallBack = (data) => {
      const locale = getLocale().replace('-', '_'); // java locale format
      const { paddingConfig, prefixConfig } = this.state;

      let checkBalance;
      if (data !== undefined ? data.callEnhanced : this.state.callEnhanced) {
        checkBalance = 'checkEnhancedBalance';
      } else checkBalance = 'checkBalance';


      const getBalanceUrl = `${process.env.REACT_APP_POST_DOMAIN}/${window.env.contextPath}/api/${checkBalance}`;

      const pin = data !== undefined ? data.pin : this.state.pin;
      let pinFinal;
      if (pin.length > 4) {
        switch (this.state.pinTruncate) {
          case 'none':
            pinFinal = pin;
            break;
          case 'first4':
            pinFinal = pin.substring(0, 4);
            break;
          case 'last4':
          default:
            pinFinal = pin.substring(pin.length - 4);
            break;
        }
      } else {
        pinFinal = pin;
      }

      // Get Card Number input by the user
      let cardNumber = data !== undefined ? data.cardNo : this.state.cardNo;
      const { prefixString, exemptLengths } = prefixConfig;
      const { paddingCharacter, padUpToLength } = paddingConfig;

      // Card Number Padding
      if (paddingCharacter.trim() !== '' || padUpToLength !== null) {
        cardNumber = padCardNumber(cardNumber, paddingConfig, prefixConfig);
      }
      
      // Card Number Prefix
      if (prefixString.trim() !== '' && !exemptLengths.includes(cardNumber.length)) {
        cardNumber = prefixCardNumber(cardNumber, prefixString);
      }
      
      this.setState({ loadingResults: true, showResults: false }, () => {
        axios
          .post(getBalanceUrl, {
            cardNumber,
            pinNumber: pinFinal,
            host: data !== undefined ? data.host : this.state.host,
            locale,
            captchaType:
              data !== undefined ? data.showCaptcha : this.state.showCaptcha,
            captcha: this.state.showCaptcha === 3 ? data.token : this.state.passedCaptchaValue,
            landTime: this.state.landingTime,
          })
          .then((response) => {
            this.state.clearCardNumberOnSuccess
              ? this.clear()
              : this.clearAllButCard();

            if (response.status === 200) {
              const resData = response.data;

              // make sure the response object actually has data
              if (Object.keys(resData).length >= 1) {
                const { resultList } = resData;

                // make sure the resultList actually has data
                if (resultList && resultList.length >= 1) {
                  const result = resultList[0];

                  const {
                    balance, requestedCurrency, applepassLink, thirdPartyLink, googlePayLink, cardEOV,
                  } = result;

                  // enhancedBalance needs to also set transactions
                  if (this.state.callEnhanced) {
                    const { transactions } = result;
                    this.updateTransactionCurrencies(transactions);
                  }
                  // these get set always
                  return this.setState({
                    showResults: true,
                    scrollBottom: true,
                    balance,
                    requestedCurrency,
                    applepassLink,
                    gpayLink: googlePayLink,
                    thirdPartyLink,
                    cardEOV,
                    showError: false,
                    cardNoForDisplay: cardNumber.slice(cardNumber.length - 4),
                    cardNoFullLength: cardNumber
                      .replace(
                        new RegExp(
                          `^(\\d{${cardNumber.length % 4 ? 0 : cardNumber.length % 4
                          }})(\\d{4})`,
                          'g',
                        ),
                        '$1 $2',
                      )
                      .replace(/(\d{4})+?/gi, '$1 ')
                      .trim(),
                  });
                }
              }
              // data empty
              return this.setState({ showError: true, showResults: false });
            }
            // bad response
            return this.setState({ showError: true, showResults: false });
          })
          // promise resolved error
          .catch((error) => {
            if (data !== undefined) {
              data.clearCardNumberOnError
                ? this.clear()
                : this.clearAllButCard();
            } else {
              this.state.clearCardNumberOnError
                ? this.clear()
                : this.clearAllButCard();
            }
            return this.setState({ showError: true, showResults: false });
          });
      });
    };

    scrollToBottom = () => {
      const node = ReactDOM.findDOMNode(this);
      // Get child nodes
      if (node instanceof HTMLElement) {
        const target = node.querySelector('#results');
        if (target) {
          target.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
      }
    };

    clear = () => {
      // reset defaults
      this.setState({
        loadingResults: false,
        passedCaptcha: false,
        cardNo: '',
        pin: '',
        touched: {
          cardNo: false,
          pin: false,
        },
        focused: {
          cardNo: false,
          pin: false,
        },
      });

      // reset captcha
      const { showCaptcha } = this.state;
      if (showCaptcha !== 3 && showCaptcha !== 0) {
        const { captchaRef } = this.state;
        captchaRef.current.reset();
      }
    };

    clearAllButCard = () => {
      // reset defaults
      this.setState({
        loadingResults: false,
        passedCaptcha: false,
        pin: '',
        touched: {
          cardNo: false,
          pin: false,
        },
        focused: {
          cardNo: true,
          pin: false,
        },
      });

      // reset captcha
      this.state.captchaRef.current.reset();
    };

    handleToggle = (name: string) => {
      const prevNameState = this.state[name];
      if (!prevNameState) this.setState({ [name]: !prevNameState });
    };

    // handles if field has ever been touched (delays validation until user leaves field)
    handleBlur = (field: string) => this.setState((prevState: State) => {
      const touchedCopy = {
        ...prevState.touched,
        [field]: true,
      };
      const focusedCopy = {
        ...prevState.focused,
        [field]: false,
      }
      return { touched: touchedCopy, focused: focusedCopy };
    });

    // handles if user is currently focused on field
    handleFocus = (field: string) => this.setState((prevState: State) => {
      const focusedCopy = {
        ...prevState.focused,
        [field]: true,
      };
      return { focused: focusedCopy };
    });

    handleChange = (
      name: string,
      value: string,
      formatCardNum: Object = { enabled: false, digitsBetweenSpaces: 0 },
    ) => {
      if (formatCardNum.enabled) {
        const finalValue = value
          .replace(/[^\dA-Z]/g, '')
          .replace(/(.{4})/g, '$1 ')
          .trim();
        this.setState({ [name]: finalValue });
      } else {
        this.setState({ [name]: value });
      }
    };

    // TEMPORARY FUNCTION
    updateTransactionCurrencies = (
      transactions,
    ) => {
      if (transactions !== null) {
        transactions.forEach(transaction => {
          transaction.thistInCurrency = i18n.t(`transaction.currency.${transaction.thistInCurrency}`);
        });
        this.setState({
          transactions,
        });
      }
    };

    handleUpdateCardConfig = (
      length: string | Array<number>,
      prefixConfig: Object,
      paddingConfig: Object,
      formatCardNum: Object,
    ) => this.setState({
      cardLength: length,
      prefixConfig,
      paddingConfig,
      formatCardNum,
    });

    handleUpdatePinConfig = (
      length: string | Array<number>,
      pinType: string,
      pinTruncate: string,
    ) => this.setState({ pinLength: length, pinType, pinTruncate });

    // if captcha ref returns a value, user is verified
    handleVerifyCaptcha = (value: string) => {
      this.setState({
        passedCaptcha: value !== null,
        passedCaptchaValue: value,
      });
      if (this.state.showCaptcha === 2) {
        this.handleSubmitCallBack();
      }
    };

    render() {
      const {
        cardNo,
        pin,
        cardLength,
        pinLength,
        pinType,
        passedCaptcha,
        loadingResults,
        formatCardNum,
      } = this.state;
      const newProps = { ...this.props, ...PassedProps };
      const { title, favicon } = newProps;
      return (
        <ClientInfo.Consumer>
          {({ clientInfo }) => {
            const { showPin, showCaptcha, language } = clientInfo;
            const errors = hasInvalidInputs(
              cardNo,
              pin,
              cardLength,
              pinLength,
              showPin,
              pinType,
              formatCardNum,
            );

            return (
              <ClientPage.Provider
                value={{
                  pageData: this.state,
                  actions: {
                    handleToggle: this.handleToggle,
                    handleChange: this.handleChange,
                    handleBlur: this.handleBlur,
                    handleFocus: this.handleFocus,
                    handleUpdateCardConfig: this.handleUpdateCardConfig,
                    handleUpdatePinConfig: this.handleUpdatePinConfig,
                    handleSubmit: this.handleSubmit,
                    handleVerifyCaptcha: this.handleVerifyCaptcha,
                    handleVerifyCaptchaV3: this.handleVerifyCaptchaV3,
                  },
                  formState: {
                    isEnabled: isEnabled(
                      errors,
                      showCaptcha,
                      passedCaptcha,
                      loadingResults,
                    ),
                    errors,
                  },
                }}
              >
                <Helmet>
                  <title>{i18n.t(title)}</title>
                  <link
                    rel='shortcut icon'
                    name='favicon'
                    href={process.env.PUBLIC_URL + favicon}
                  />
                  <html lang={language} />
                </Helmet>
                <PassedComponent />
              </ClientPage.Provider>
            );
          }}
        </ClientInfo.Consumer>
      );
    }
  }
  return WithClientPage;
};

export default withClientPage;
